import * as React from 'react';
import { gql } from '@apollo/client';
import useBookingMutation from 'booking/hooks/useBookingMutation';
import type { BookingSessionInput } from '@bondvet/types/booking';
import { useDispatch, useSelector } from 'react-redux';
import isEmail from 'validator/lib/isEmail';
import {
    getClientId,
    getSessionId,
    getProvider,
    getAge,
    getPet,
    getSpecies,
    getFirstName,
    getLastName,
    getEmail,
    getDate,
    getTime,
    getAnimalId,
    getPhoneNumber,
    getReason,
    getNotes,
    getSmsPreferences,
    getCityId,
    getLocationId,
} from 'booking/redux/selectors';
import { setSessionId } from 'booking/redux/actions';
import useStep from './useStep';

const mutation = gql`
    mutation trackBookingSession($input: BookingSessionInput!) {
        trackBookingSession(input: $input) {
            success
            id
        }
    }
`;

type TrackBookingSessionVariables = {
    input: BookingSessionInput;
};

type TrackBookingSessionQuery = {
    trackBookingSession: {
        id: string;
        success: boolean;
    };
};

type TrackBookingSession = {
    run: (input: BookingSessionInput) => void;
};

// we don't need one debounce timer per component, but only a global one
let debounceTimer: ReturnType<typeof window.setTimeout> | null = null;

export default function useTrackBookingSessions(
    runOnMount = false,
): TrackBookingSession {
    const dispatch = useDispatch();

    const sessionId = useSelector(getSessionId);
    const userId = useSelector(getClientId);
    const email = useSelector(getEmail);
    const firstName = useSelector(getFirstName);
    const lastName = useSelector(getLastName);
    const cityId = useSelector(getCityId);
    const locationId = useSelector(getLocationId);
    const provider = useSelector(getProvider);
    const petName = useSelector(getPet);
    const petSpecies = useSelector(getSpecies);
    const petAge = useSelector(getAge);
    const date = useSelector(getDate);
    const time = useSelector(getTime);
    const animalId = useSelector(getAnimalId);
    const phoneNumber = useSelector(getPhoneNumber);
    const reason = useSelector(getReason);
    const notes = useSelector(getNotes);
    const smsPreferences = useSelector(getSmsPreferences);

    const { step, stepAllowed } = useStep(runOnMount);

    const [trackBookingSession, { data, error }] = useBookingMutation<
        TrackBookingSessionQuery,
        TrackBookingSessionVariables
    >(mutation);

    const run = React.useCallback(
        async (input: BookingSessionInput) => {
            if (!input.email || !isEmail(input.email)) {
                return;
            }

            if (!step || !stepAllowed) {
                // how could this happen?
                return;
            }

            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            debounceTimer = setTimeout(async () => {
                debounceTimer = null;

                try {
                    await trackBookingSession({
                        variables: {
                            input: {
                                ...input,
                                step,
                            },
                        },
                    });
                } catch (sendError) {
                    console.warn(
                        'error keeping track of booking state:',
                        (sendError as Error).message,
                    );
                }
            }, 400);
        },
        [trackBookingSession, step, stepAllowed],
    );

    React.useEffect(() => {
        if (email && runOnMount) {
            run({
                _id: sessionId,
                userId,
                email,
                firstName,
                lastName,
                phoneNumber,
                cityId,
                date,
                time,
                locationId,
                providerId: provider?.id,
                animalId,
                petName,
                petSpecies,
                petAge,
                reason,
                notes,
                smsMarketing: smsPreferences,
                smsTransactional: smsPreferences,
                smsConversational: smsPreferences,
                // even though it doesn't get used
                // we want to "send" the step here,
                // so we know we track every booking step correctly
                step,
            }).then();
        }
    }, [
        animalId,
        date,
        sessionId,
        time,
        userId,
        email,
        firstName,
        lastName,
        cityId,
        locationId,
        provider,
        petName,
        petSpecies,
        petAge,
        run,
        runOnMount,
        phoneNumber,
        reason,
        notes,
        smsPreferences,
        step,
    ]);

    React.useEffect(() => {
        if (error) {
            // log the error
            console.error(error);
        }
    }, [error]);

    React.useEffect(() => {
        const receivedId = data?.trackBookingSession?.id;

        if (receivedId && receivedId !== sessionId) {
            dispatch(setSessionId(data.trackBookingSession.id));
        }
    }, [data, dispatch, sessionId]);

    return { run };
}
